import { useEffect, useState } from "react";
import { useToast } from "../../../../../_context/ToastContext/ToastContext";
import { useDynamicForm } from "../../../../../_components/form/hooks/useDynamicForm";
import { useCompanyFormMutations } from "./useCompanyFormMutations";
import { formatCompanySuccessMessage } from "../utils/companyFormRules";

/**
 * State Management Layer - wires the dynamic-form engine (useDynamicForm)
 * together with everything else CompanyFormModal needs to track (picked
 * logo, the close-confirmation flow), and owns the submit orchestration
 * (calling into the Data/API layer, showing toasts, resetting/closing). The
 * UI layer (index.jsx) only reads from and calls into what this hook
 * returns - no business decisions live there.
 */
export function useCompanyFormState(
  processedFormSchema,
  { mode, rowData, isOpen, isSelfServeUser, onClose, onSuccess, refetchFilters }
) {
  const { showToast } = useToast();
  const { submitCompany, isSubmitting } = useCompanyFormMutations();

  const dynamicForm = useDynamicForm(processedFormSchema, { mode, rowData });
  const {
    handleSubmit: rhfHandleSubmit,
    reset,
    formState: { isDirty },
  } = dynamicForm;

  const [imageFile, setImageFile] = useState(null);
  const [shouldDeleteImage, setShouldDeleteImage] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  // react-hook-form already tracks whether any field changed from its
  // defaults (isDirty, flips on a single keystroke) - imageFile/
  // shouldDeleteImage are the only "has this form changed" state that live
  // outside the form (logo upload isn't a form field).
  const hasUnsavedChanges = isDirty || Boolean(imageFile) || shouldDeleteImage;

  useEffect(() => {
    if (isOpen) {
      setShowCloseConfirmation(false);
    }
  }, [isOpen]);

  function resetAndClose() {
    reset();
    setImageFile(null);
    setShouldDeleteImage(false);
    setShowCloseConfirmation(false);
    onClose();
  }

  // The single dismiss guard - called by ModalShell's onRequestClose (ESC,
  // overlay click, header close) AND the footer Close button, so all three
  // dismiss paths behave identically.
  function attemptClose() {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);
    } else {
      resetAndClose();
    }
  }

  function cancelDiscard() {
    setShowCloseConfirmation(false);
  }

  const onSubmit = rhfHandleSubmit(async (validData) => {
    try {
      const response = await submitCompany({
        mode,
        isSelfServeUser,
        validData,
        rowData,
        imageFile,
        shouldDeleteImage,
      });

      showToast(formatCompanySuccessMessage(response, mode), "success");
      // Passes the created/updated company back to the caller - needed by
      // the entity-modal opener (openModal("create_company")) so an inline
      // picker can auto-select the record it just created instead of
      // making the user search for it again. Existing callers that ignore
      // the argument (QuickCreateModals etc.) are unaffected.
      onSuccess?.(response?.data);
      refetchFilters?.();
      resetAndClose();
    } catch (error) {
      console.error("Error:", error);
      showToast(`${error?.data?.message}`, "error");
    }
  }, () => {
    // A required field below the drawer's fold (e.g. "Company manager") left
    // Create looking like it silently did nothing. react-hook-form can't
    // focus the custom selects, so scroll the first visible error into view.
    setTimeout(() => {
      const firstError = [...document.querySelectorAll(".company-form-modal--right-docked [class*='error']")].find(
        (el) => el.offsetParent && el.textContent.trim()
      );
      firstError?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 0);
  });

  return {
    ...dynamicForm,
    imageFile,
    setImageFile,
    shouldDeleteImage,
    setShouldDeleteImage,
    showCloseConfirmation,
    hasUnsavedChanges,
    isSubmitting,
    attemptClose,
    confirmDiscard: resetAndClose,
    cancelDiscard,
    onSubmit,
  };
}
